import React, {
  useEffect, useState, useCallback,
  useRef
} from 'react';
import PropTypes from 'prop-types';
import CircularLoader from '../CircularLoader';
import DigitalDispatchGoogleMap from './DigitalDispatchGoogleMap';
import DigitalDispatchDriverMarker from './DigitalDispatchDriverMarker';

const DEFAULT_ZOOM = 10;
const INCREASED_ZOOM = 13;

function DispatchLocationUpdatesMap({
  dispatchPickUpLocation,
  dispatchDropOffLocation,
  dispatchLocationUpdates,
  driverWaypointHistorySwitchState,
  isLoading
}) {
  const map = useRef(null);
  const [
    startLocationToEndLocationDirectionResponse,
    setStartLocationToEndLocationDirectionResponse
  ] = useState(null);
  const [
    driverRouteWaypoints, setDriverRouteWayPoints
  ] = useState(dispatchLocationUpdates ?? null);
  const [mostRecentDriverLocation, setMostRecentDriverLocation] = useState(
    driverRouteWaypoints?.[0] ?? null
  );
  const [center, setCenter] = useState({
    lat: dispatchPickUpLocation.latitude,
    lng: dispatchPickUpLocation.longitude
  } ?? null);
  const [zoom, setZoom] = useState(DEFAULT_ZOOM);

  useEffect(() => {
    if (dispatchLocationUpdates) setDriverRouteWayPoints(dispatchLocationUpdates);
  }, [dispatchLocationUpdates]);

  useEffect(() => {
    if (driverRouteWaypoints?.length) setMostRecentDriverLocation(driverRouteWaypoints[0]);
  }, [driverRouteWaypoints]);

  useEffect(() => {
    if (mostRecentDriverLocation) {
      setCenter({
        lat: mostRecentDriverLocation?.assignedVehicle?.geoPoint?.latitude,
        lng: mostRecentDriverLocation?.assignedVehicle?.geoPoint?.longitude
      });
      setZoom(INCREASED_ZOOM);
    }
  }, [mostRecentDriverLocation]);

  const onLoad = useCallback((mapInstance) => {
    // Extend bounds on initial load to show pick-up & drop-off point
    const bounds = new window.google.maps.LatLngBounds();
    bounds.extend({
      lat: dispatchPickUpLocation.latitude,
      lng: dispatchPickUpLocation.longitude
    });
    bounds.extend({
      lat: dispatchDropOffLocation.latitude,
      lng: dispatchDropOffLocation.longitude
    });
    mapInstance.fitBounds(bounds);
    map.current = mapInstance;
  }, []);

  const onUnmount = useCallback(() => {
    map.current = null;
  }, []);

  /**
  * @param {google.maps.DirectionsResult} response
  * @return {void}
  */
  const directionsServiceCallback = (response) => {
    if (response) {
      setStartLocationToEndLocationDirectionResponse(response);
    }
  };

  return (
    isLoading
      ? <CircularLoader />
      : (
        <DigitalDispatchGoogleMap
          zoom={zoom}
          center={center}
          onLoad={onLoad}
          onUnmount={onUnmount}
          dispatchPickUpLocation={dispatchPickUpLocation}
          dispatchDropOffLocation={dispatchDropOffLocation}
          startLocationToEndLocationDirectionResponse={startLocationToEndLocationDirectionResponse}
          directionsServiceCallback={directionsServiceCallback}
        >
          <DigitalDispatchDriverMarker
            driverRouteWaypoints={driverRouteWaypoints}
            mostRecentDriverLocation={mostRecentDriverLocation}
            driverWaypointHistorySwitchState={driverWaypointHistorySwitchState}
          />
        </DigitalDispatchGoogleMap>
      )
  );
}

DispatchLocationUpdatesMap.propTypes = {
  dispatchPickUpLocation: PropTypes.shape({
    addressId: PropTypes.number,
    fullAddress: PropTypes.string,
    googleLocationId: PropTypes.string,
    address: PropTypes.string,
    city: PropTypes.string,
    stateProvidence: PropTypes.string,
    postalCode: PropTypes.string,
    latitude: PropTypes.number,
    longitude: PropTypes.number,
    addressTypeId: PropTypes.number,
    locationName: PropTypes.string
  }).isRequired,
  dispatchDropOffLocation: PropTypes.shape({
    addressId: PropTypes.number,
    fullAddress: PropTypes.string,
    googleLocationId: PropTypes.string,
    address: PropTypes.string,
    city: PropTypes.string,
    stateProvidence: PropTypes.string,
    postalCode: PropTypes.string,
    latitude: PropTypes.number,
    longitude: PropTypes.number,
    addressTypeId: PropTypes.number,
    locationName: PropTypes.string
  }).isRequired,
  dispatchLocationUpdates: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    bsCompanyId: PropTypes.number,
    providerId: PropTypes.string,
    eta: PropTypes.string,
    jobId: PropTypes.string,
    locationId: PropTypes.string,
    motorClubJobId: PropTypes.number,
    motorClubKey: PropTypes.string,
    phone: PropTypes.string,
    purchaseOrderId: PropTypes.string,
    dispatchId: PropTypes.string,
    responseId: PropTypes.string,
    zip: PropTypes.string,
    sourceType: PropTypes.string,
    digitalProviderNetwork: PropTypes.string,
    assignedVehicle: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      geoPoint: PropTypes.shape({
        latitude: PropTypes.number,
        longitude: PropTypes.number
      }),
      gpsLastUpdate: PropTypes.string,
      direction: PropTypes.number
    }),
    assignedDriver: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      description: PropTypes.string
    }),
  })),
  driverWaypointHistorySwitchState: PropTypes.bool,
  isLoading: PropTypes.bool
};

export default DispatchLocationUpdatesMap;
